import React, { useCallback } from "react";
import styled from "styled-components";

import {
  breakpoints,
  colors,
  Text,
  Button as AsurionButton,
  Size,
} from "@trailhead/asurion-ui";
import {
  FlexColumnContainer,
  FlexRowContainer,
  useWindowDimensions,
} from "@trailhead/core-ui";
import { useSanitizedTranslation } from "@trailhead/i18n";
import { useChatClient } from "@trailhead/chat";
import { trackEvent } from "@trailhead/analytics";

import { ExpertChat } from "./ExpertChat";

const IntroHeaderOzmoVerizonContainer = styled(FlexRowContainer)`
  width: 100vw;
  position: relative;
  justify-content: center;
  background-color: ${colors.neutralBrightest};
  margin: 0 -100vw;
  height: 270px;
  padding: 36px 0px 48px 0px;

  ${breakpoints.md} {
    height: 315px;
    padding: 24px 0px 64px 0px;
  }

  ${breakpoints.md2} {
    height: 378px;
    padding: 24px 0px;
  }

  ${breakpoints.lg} {
    height: 482px;
    padding: 120px 0px 200px 0px;
  }
`;

const ContentContainer = styled(FlexColumnContainer)`
  width: 100%;
  max-width: 1200px;
  position: relative;
  align-items: center;

  ${breakpoints.lg} {
    align-items: start;
    max-width: 1150px;
  }

  ${breakpoints.xl} {
    max-width: 1100px;
  }

  ${breakpoints.xxl} {
    max-width: 1350px;
  }
`;

const HeaderButtonContainer = styled(FlexColumnContainer)`
  align-items: center;
  gap: 24px;
  padding: 0px 16px;

  ${breakpoints.sm} {
    width: 343px;
    padding: unset;
  }

  ${breakpoints.md} {
    width: unset;
    padding-top: 61px;
  }

  ${breakpoints.md2} {
    padding-top: 92.5px;
  }

  ${breakpoints.lg} {
    padding-top: unset;
    gap: 12px;
    align-items: flex-start;
  }
`;

const HeaderContainer = styled(FlexColumnContainer)`
  align-items: center;
  gap: 12px;
  text-align: center;

  ${breakpoints.lg} {
    width: 600px;
    text-align: unset;
  }
`;

const HeaderTitle = styled(Text)``;

const ButtonContainer = styled(FlexColumnContainer)`
  width: 218px;
  z-index: 1;
`;
const AsurionButtonWrapper = styled(AsurionButton)``;

const ExpertChatContainer = styled(FlexColumnContainer)`
  width: 90%;
  position: relative;
  align-items: center;
  top: 70px;

  ${breakpoints.md} {
    width: 100%;
    top: 100px;
  }

  ${breakpoints.md2} {
    top: 130px;
  }

  ${breakpoints.lg} {
    position: absolute;
    align-items: end;
    top: unset;
  }

  ${breakpoints.xl} {
    left: 40px;
    top: 55px;
  }

  ${breakpoints.xxl} {
    left: 170px;
  }
`;

export const IntroHeaderOzmoVerizon = () => {
  const t = useSanitizedTranslation();
  const ChatClient = useChatClient();

  const { width } = useWindowDimensions();
  const isDesktop = width >= Size.LG;
  const isTablet = width >= Size.MD;

  const renderSubtitle = () => (
    <Text
      size={isDesktop ? 6 : isTablet ? 4 : 3}
      weight={isDesktop ? "heavy" : "base"}
    >
      {t("We’re here for you 24/7.")}
    </Text>
  );

  const onChatButtonClick = useCallback(() => {
    trackEvent("Pathfinder_L_Chat_Button_Clicked");
    ChatClient.showMessaging();
    ChatClient.openMessagingOverlay();
  }, [ChatClient]);

  return (
    <IntroHeaderOzmoVerizonContainer>
      <ContentContainer>
        <HeaderButtonContainer>
          <HeaderContainer>
            <HeaderTitle size={isDesktop ? 6 : 5} weight="feather">
              {t("Set up your device with expert help. ")}
              {isDesktop ? renderSubtitle() : null}
            </HeaderTitle>
            {!isDesktop ? renderSubtitle() : null}
          </HeaderContainer>
          <ButtonContainer>
            <AsurionButtonWrapper
              iconSrc="ChatBubbleCircle"
              size="medium"
              onClick={onChatButtonClick}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: t("Chat with an expert"),
                }}
              />
            </AsurionButtonWrapper>
          </ButtonContainer>
        </HeaderButtonContainer>
        <ExpertChatContainer>
          <ExpertChat chatClient={ChatClient} />
        </ExpertChatContainer>
      </ContentContainer>
    </IntroHeaderOzmoVerizonContainer>
  );
};
